import { Injectable, NgZone, inject } from '@angular/core';

import { BehaviorSubject, Observable, Subject, catchError, filter, of, take, timeout } from 'rxjs';

import { WINDOW } from '../browser/window/window.token';
import { Logger } from '../logging/logger';
import { UrlService } from '../navigation/url.service';
import { toJson } from '../utils/convert';
import { EventsService, VanillaEventNames } from '../utils/events.service';
import { UtilsService } from '../utils/utils.service';
import { NativeAppConfig } from './native-app.client-config';
import { NativeEvent, NativeEventPlaytech, NativeWindow } from './native-app.models';

/**
 * @whatItDoes Indicates whether we are in app/wrapper context, and has information about app name and product id. Also allows communication from/to an app.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class NativeAppService {
    readonly #window = inject(WINDOW);
    private readonly config = inject(NativeAppConfig);
    private readonly logger = inject(Logger);
    private readonly utils = inject(UtilsService);
    private readonly zone = inject(NgZone);
    private readonly eventsService = inject(EventsService);
    private readonly urlService = inject(UrlService);

    private readonly nativeEvents: Subject<NativeEvent> = new Subject<NativeEvent>();
    private readonly tracingBlacklistPattern = new RegExp(this.config.tracingBlacklistPattern ?? '(?!)');

    private twaPort: MessagePort | undefined = undefined;
    private twaEventQueue: string[] | null = null;
    private $twaCCBInitialized = new BehaviorSubject<boolean>(false);
    private isTwa: boolean = true;
    private twaMessageHandler: (event: MessageEvent) => void;

    constructor() {
        this.initTwaCommunication();
    }

    get context(): string {
        if (this.#window.self !== this.#window.top) {
            return 'iframe';
        }

        return 'default';
    }

    /**
     * Shortcut for `isNativeApp || isNativeWrapper || isDownloadClient || isTerminal`.
     */
    get isNative(): boolean {
        return this.config.isNative;
    }

    get isNativeApp(): boolean {
        return this.config.isNativeApp;
    }

    get isNativeWrapper(): boolean {
        return this.config.isNativeWrapper;
    }

    get isNativeWrapperODR(): boolean {
        return this.config.isNativeWrapper && this.config.applicationName.toLowerCase().includes('odr');
    }

    get isDownloadClient(): boolean {
        return this.config.isDownloadClient;
    }

    get isDownloadClientApp(): boolean {
        return this.config.isDownloadClientApp;
    }

    get isDownloadClientWrapper(): boolean {
        return this.config.isDownloadClientWrapper;
    }

    get isTerminal(): boolean {
        return this.config.isTerminal;
    }

    get product(): string {
        return this.config.product;
    }

    get applicationName(): string {
        return this.config.applicationName;
    }

    get nativeScheme(): string {
        const override = this.#window['__NativeSchemeOverride'];

        return override ?? 'bwin://';
    }

    get playtechNativeScheme(): string {
        return 'htcmd:';
    }

    get nativeMode(): string {
        return this.config.nativeMode;
    }

    get htcmdSchemeEnabled(): boolean | undefined {
        return this.config.htcmdSchemeEnabled;
    }

    get sendPageClosedEvent(): boolean | undefined {
        return this.config.sendPageClosedEvent;
    }

    /** Subscribe to events sent by the native app. */
    get eventsFromNative(): Observable<NativeEvent> {
        return this.nativeEvents;
    }

    /** Send an CCB event to native app. */
    sendToNative(event: NativeEvent) {
        const message = 'Sending CCB via ';

        if (this.config.disabledEvents.includes(event.eventName)) {
            this.logEvent(event, 'Not sending disabled CCB', '', false);
            return;
        }

        const window = <NativeWindow>(<any>this.#window);

        event = { ...event, id: this.utils.generateGuid() };

        this.sendToTwa(event);

        if (window.messageToZendesk) {
            this.logEvent(event, message, 'messageToZendesk', false);
            window.messageToZendesk(event);
        }

        if (window.messageToAbTest) {
            this.logEvent(event, message, 'messageToAbTest', false);
            window.messageToAbTest(event);
        }

        if (window.messageToNative) {
            this.logEvent(event, message, 'messageToNative');
            window.messageToNative(event);
        } else if (window.external?.NativeDispatch) {
            this.logEvent(event, message, 'external.NativeDispatch');
            window.external.NativeDispatch(event.eventName, JSON.stringify(event.parameters));
        } else if (window.webkit?.messageHandlers?.observer?.postMessage) {
            this.logEvent(event, message, 'webkit.messageHandlers.observer.postMessage');
            window.webkit.messageHandlers.observer.postMessage(event.eventName, JSON.stringify(event.parameters));
        }

        this.eventsService.raise({
            eventName: VanillaEventNames.EVENT_SENT_TO_NATIVE,
            data: {
                eventName: event.eventName,
                parameters: event.parameters,
            },
        });
    }

    sendToPlaytech(event: NativeEventPlaytech) {
        const message = 'Sending CCB via ';

        if (this.config.disabledEvents.includes(event.message)) {
            this.logEvent(event, 'Not sending disabled CCB for playtech', '', false);
            return;
        }

        const window = <NativeWindow>(<any>this.#window);
        if (window.messageToNative) {
            this.logEvent(event, message, 'messageToNative');
            window.messageToNative(event);
        } else if (window.external?.NativeDispatch) {
            this.logEvent(event, message, 'external.NativeDispatch');
            window.external.NativeDispatch(event.message, JSON.stringify(event.data));
        } else if (window.webkit?.messageHandlers?.observer?.postMessage) {
            this.logEvent(event, message, 'webkit.messageHandlers.observer.postMessage');
            window.webkit.messageHandlers.observer.postMessage(event.message, JSON.stringify(event.data));
        }

        this.eventsService.raise({
            eventName: VanillaEventNames.EVENT_SENT_TO_PLAYTECH,
            data: {
                eventName: event.message,
                parameters: event.data,
            },
        });
    }

    onReceivedEventFromNative(event: NativeEvent | string) {
        this.zone.run(() => {
            const nativeEvent = toJson(event);
            this.nativeEvents.next(nativeEvent);
            this.logEvent(nativeEvent, 'Received CCB');
        });
    }

    private initTwaCommunication() {
        this.twaMessageHandler = (event: MessageEvent) => {
            if (event.origin.startsWith('android-app://') && !event.data && event.ports.length > 0) {
                this.logger.info('Handshake message received — channel is ready.');
                this.twaPort = event.ports[0]!;
            }

            try {
                const parsedUrl = this.urlService.parse(event.origin.replace('android-app://', ''));
                if (!parsedUrl.isSameTopDomain) {
                    return;
                }
            } catch (e) {
                this.logger.errorRemote('Error parsing event origin', e);
                return;
            }

            if (this.twaPort) {
                // Listen for incoming messages from TWA
                this.twaPort.onmessage = (msgEvent: MessageEvent) => {
                    this.onReceivedEventFromNative(msgEvent.data);
                };
                this.$twaCCBInitialized.next(true);
            }

            // cleanup: no need to listen for more twaPort messages, port will still listen for messages from TWA.
            this.#window.removeEventListener('message', this.twaMessageHandler);
        };

        this.#window.addEventListener('message', this.twaMessageHandler);
    }

    private sendToTwa(event: NativeEvent) {
        let message = JSON.stringify(event); // TWA expexts strings only;
        if (this.twaPort) {
            this.twaPort.postMessage(message);
            return;
        }

        // If TWA isn't ready yet, queue the event and set up a single subscription
        if (!this.twaEventQueue) {
            this.twaEventQueue = [];
            this.setupTwaQueueSubscription();
        }

        if (this.isTwa) {
            this.twaEventQueue.push(message);
        }
    }

    private setupTwaQueueSubscription() {
        this.$twaCCBInitialized
            .pipe(
                timeout(20000),
                filter(Boolean),
                take(1),
                catchError(() => {
                    //Cleanup if TWA isn't ready after 20 seconds, means it's not TWA.
                    this.isTwa = false;
                    this.twaEventQueue = null;
                    window.removeEventListener('message', this.twaMessageHandler);
                    return of(false);
                }),
            )
            .subscribe(() => {
                if (this.twaEventQueue) {
                    const port = this.twaPort;
                    if (port) {
                        this.twaEventQueue.forEach((event) => {
                            port.postMessage(event);
                        });
                    } else {
                        this.logger.error('TWA port unexpectedly undefined when processing queue.');
                    }
                    this.twaEventQueue = null;
                }
            });
    }

    private logEvent(event: NativeEvent, desc: string, channel: string = '', shouldTrace: boolean = true) {
        const message = `${desc}${channel}: `;

        if (this.config.enableCCBDebug) {
            this.logger.info(message, event);
        }

        if (this.config.enableCCBTracing && shouldTrace && !this.tracingBlacklistPattern.test(event?.eventName)) {
            const remoteMessage =
                message +
                JSON.stringify(event, (key, val) => {
                    if (key.toLowerCase().indexOf('password') !== -1) {
                        return '***';
                    }

                    return val;
                });

            this.logger.infoRemote(remoteMessage);
        }
    }
}
